import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useSlugNavigate } from '../hooks/useNavigation'
import DashboardLayout from '../components/DashboardLayout'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:6200'

export default function WorkspacesPage() {
  const { user, token, refreshUser } = useAuth()
  const navigate = useSlugNavigate()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [createFormData, setCreateFormData] = useState({
    name: '',
    tenancy_type: 'SERVICE_PROVIDER',
    description: ''
  })

  const handleCloseCreateModal = () => {
    setShowCreateModal(false)
    setCreateFormData({
      name: '',
      tenancy_type: 'SERVICE_PROVIDER',
      description: ''
    })
    setError('')
  }

  const handleCreateWorkspace = async () => {
    if (!createFormData.name.trim()) {
      setError('Organization name is required')
      return
    }

    setLoading(true)
    setError('')

    try {
      const response = await fetch(`${API_URL}/api/v1/tenancies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: createFormData.name,
          tenancy_type: createFormData.tenancy_type,
          description: createFormData.description,
          user_role: 'OWNER'
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create organization')
      }

      // Refresh user data to update organization list
      await refreshUser()

      // Close modal and reset form
      handleCloseCreateModal()

      // Navigate to the new organization management page
      navigate(`/settings/organizations/${data.tenancy.id}`)
    } catch (err) {
      setError(err.message || 'Failed to create organization')
    } finally {
      setLoading(false)
    }
  }

  return (
    <DashboardLayout variant="settings">
      {/* Top Navigation Bar */}
      <header className="flex items-center justify-between whitespace-nowrap border-b border-solid border-[#dbe0e6] bg-white px-8 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-4 text-[#111418]">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#617589] hover:text-primary transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">arrow_back</span>
            <span className="text-sm font-medium">Back to Dashboard</span>
          </button>
        </div>
      </header>

      {/* Page Content */}
      <div className="p-8 max-w-6xl mx-auto w-full">
        {/* Page Heading */}
        <div className="mb-8">
          <h1 className="text-[#111418] text-3xl font-black leading-tight tracking-[-0.033em] mb-2">
            Organizations
          </h1>
          <p className="text-[#617589] text-base font-normal leading-normal">
            Manage your organizations and connected companies
          </p>
        </div>

        {/* My Organizations */}
        <div className="mb-8">
          <h2 className="text-[#111418] text-xl font-bold mb-4">My Organizations</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {user?.tenancies?.map((tenancy) => (
              <div
                key={tenancy.id}
                className={`bg-white rounded-xl border-2 p-6 shadow-sm transition-all ${
                  tenancy.id === user.active_tenancy_id
                    ? 'border-primary'
                    : 'border-[#dbe0e6] hover:border-primary/50'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <span className="material-symbols-outlined text-primary text-2xl">business</span>
                    </div>
                    <div>
                      <h3 className="text-[#111418] font-bold text-lg">{tenancy.name}</h3>
                      <p className="text-[#617589] text-sm">
                        {tenancy.tenancy_type === 'SERVICE_PROVIDER' ? 'Service Provider' : 'Client'}
                      </p>
                    </div>
                  </div>
                  {tenancy.id === user.active_tenancy_id && (
                    <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-bold rounded">
                      Active
                    </span>
                  )}
                </div>

                {tenancy.description && (
                  <p className="text-[#617589] text-sm mb-4">{tenancy.description}</p>
                )}

                <div className="flex items-center justify-between pt-4 border-t border-[#dbe0e6]">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                      {tenancy.role}
                    </span>
                  </div>
                  <button
                    onClick={() => navigate(`/settings/organizations/${tenancy.id}`)}
                    className="text-primary text-sm font-medium hover:underline"
                  >
                    Manage
                  </button>
                </div>
              </div>
            ))}

            {/* Create New Organization Card */}
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-white rounded-xl border-2 border-dashed border-[#dbe0e6] p-6 shadow-sm transition-all hover:border-primary hover:bg-primary/5 cursor-pointer flex flex-col items-center justify-center min-h-[200px] group"
            >
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-3 group-hover:bg-primary/20 transition-colors">
                <span className="material-symbols-outlined text-primary text-2xl">add</span>
              </div>
              <h3 className="text-[#111418] font-bold text-lg mb-1">Create New Organization</h3>
              <p className="text-[#617589] text-sm text-center">
                Add another organization to manage
              </p>
            </button>
          </div>
        </div>
      </div>

      {/* Create Organization Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-[#111418] text-2xl font-bold">Create New Organization</h2>
              <button
                onClick={handleCloseCreateModal}
                className="text-[#617589] hover:text-[#111418] transition-colors"
              >
                <span className="material-symbols-outlined text-[24px]">close</span>
              </button>
            </div>

            <div className="space-y-4 mb-6">
              {/* Organization Name */}
              <label className="flex flex-col">
                <p className="text-[#111418] text-sm font-semibold leading-normal pb-2">
                  Organization Name <span className="text-red-500">*</span>
                </p>
                <input
                  className="form-input flex w-full resize-none overflow-hidden rounded-lg text-[#111418] border border-[#dbe0e6] bg-white h-12 px-4 placeholder:text-[#617589] text-base font-normal leading-normal focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-shadow"
                  placeholder="e.g., Acme Corporation"
                  required
                  type="text"
                  name="name"
                  value={createFormData.name}
                  onChange={(e) => setCreateFormData({ ...createFormData, name: e.target.value })}
                />
              </label>

              {/* Organization Type */}
              <label className="flex flex-col">
                <p className="text-[#111418] text-sm font-semibold leading-normal pb-2">
                  Organization Type <span className="text-red-500">*</span>
                </p>
                <select
                  className="form-select flex w-full resize-none overflow-hidden rounded-lg text-[#111418] border border-[#dbe0e6] bg-white h-12 px-4 text-base font-normal leading-normal focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-shadow"
                  name="tenancy_type"
                  value={createFormData.tenancy_type}
                  onChange={(e) => setCreateFormData({ ...createFormData, tenancy_type: e.target.value })}
                >
                  <option value="SERVICE_PROVIDER">Service Provider</option>
                  <option value="CLIENT">Client</option>
                </select>
              </label>

              {/* Description */}
              <label className="flex flex-col">
                <p className="text-[#111418] text-sm font-semibold leading-normal pb-2">
                  Description <span className="text-[#617589] text-xs font-normal">(Optional)</span>
                </p>
                <textarea
                  className="form-input flex w-full resize-none overflow-hidden rounded-lg text-[#111418] border border-[#dbe0e6] bg-white p-4 placeholder:text-[#617589] text-base font-normal leading-normal focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-shadow"
                  placeholder="Brief description of this organization"
                  rows="3"
                  name="description"
                  value={createFormData.description}
                  onChange={(e) => setCreateFormData({ ...createFormData, description: e.target.value })}
                />
              </label>
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 p-4 border border-red-200 mb-4">
                <div className="flex items-start">
                  <span className="material-symbols-outlined text-red-600 mr-3 text-[20px]">error</span>
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleCloseCreateModal}
                className="flex-1 px-4 py-3 border border-[#dbe0e6] text-[#111418] rounded-lg hover:bg-gray-50 transition-colors font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateWorkspace}
                disabled={loading}
                className="flex-1 px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-semibold disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Organization'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  )
}
